import React from 'react';
import { User, Users, Briefcase, Settings2, Trash2 } from 'lucide-react';
import { PseudoManager } from '../types/pseudoManager';

const PORTAL_STYLES: Record<string, string> = {
  'Connect': 'bg-pink-50 text-pink-600 border-pink-200',
  'Recruitment': 'bg-blue-50 text-blue-600 border-blue-200',
  'Employee Central': 'bg-emerald-50 text-emerald-600 border-emerald-200',
};

interface PseudoManagerCardProps {
  pseudoManager: PseudoManager;
  onManageReportees: () => void;
  onRemove: () => void;
}

export function PseudoManagerCard({ pseudoManager, onManageReportees, onRemove }: PseudoManagerCardProps) {
  const { displayName, email, jobTitle, assignedReportees, portalAccess } = pseudoManager;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <div className="flex-shrink-0 w-11 h-11 rounded-full bg-gradient-to-br from-indigo-500 to-blue-500 flex items-center justify-center">
          <User className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-slate-900 truncate">{displayName}</h3>
            <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full">
              Pseudo Manager
            </span>
          </div>
          {jobTitle && (
            <p className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5">
              <Briefcase className="w-3 h-3" />
              {jobTitle}
            </p>
          )}
          <a href={`mailto:${email}`} className="text-xs text-pink-600 hover:underline truncate block">
            {email}
          </a>
        </div>
      </div>

      {/* Portal access */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {portalAccess.map((portal) => (
          <span
            key={portal}
            className={`px-2 py-0.5 rounded-full text-xs font-medium border ${PORTAL_STYLES[portal]}`}
          >
            {portal}
          </span>
        ))}
      </div>

      {/* Assigned reportees */}
      <div className="mb-4 flex items-center gap-1.5 text-xs font-medium text-slate-500">
        <Users className="w-3.5 h-3.5" />
        {assignedReportees.length} Assigned Reportee{assignedReportees.length !== 1 ? 's' : ''}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
        <button
          onClick={onManageReportees}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-slate-700 bg-slate-50 hover:bg-slate-100 border border-slate-200 transition-colors"
        >
          <Settings2 className="w-3.5 h-3.5" />
          Manage Reportees
        </button>
        <button
          onClick={onRemove}
          title="Remove Pseudo Manager"
          className="flex items-center justify-center px-3 py-2 rounded-lg text-red-500 bg-red-50 hover:bg-red-100 border border-red-100 transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
